import { useQuery } from '@apollo/client';
import { Box, Link as MuiLink } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Typography } from '~/components/Typography';
import { AnnouncementCard } from '~/components/announcements/AnnouncementCard';
import { EmptyState } from '~/components/empty-state';
import { LoadingPane } from '~/components/loading-pane';
import { useAppContext } from '~/contexts';
import AnnouncementsIllustration from '~/images/illustrations/announcements.svg';
import { PanelAnnouncementsDocument } from './announcements.generated';
import { useMarkAnnouncementsRead } from './mark-read';

// A glance at what is recent, not the archive — the full history lives at /announcements.
const PANEL_LIMIT = 10;

export const AnnouncementList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [markRead] = useMarkAnnouncementsRead();

  // Mounted only while the announcements tab is showing, so this runs when the reader opens
  // it. cache-and-network so it paints from cache and then catches up.
  const { data, error, loading } = useQuery(PanelAnnouncementsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { page: 1, perPage: PANEL_LIMIT },
  });
  const announcements = data?.announcements.nodes ?? [];

  return (
    <LoadingPane in={loading}>
      <Box sx={{ bgcolor: 'common.white', flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        {/* Distinct from empty: a failure here would otherwise read as "nothing new". */}
        {error && (
          <Typography sx={{ color: 'error.main' }}>{t('api.common.error_generic')}</Typography>
        )}

        {/* Waits for a response — an unanswered query is not the same as nothing to show. */}
        {!error && data && announcements.length === 0 && (
          <EmptyState
            illustration={AnnouncementsIllustration}
            header={t('components.announcements.empty_state.header')}
            description={t('components.announcements.empty_state.description')}
            // The drawer sets nowrap on everything inside it.
            sx={{ whiteSpace: 'normal' }}
          />
        )}

        {announcements.map((announcement) => (
          <AnnouncementCard
            key={announcement.id}
            announcement={announcement}
            clip
            href={`/networks/${currentNetwork.id}/announcements/${announcement.id}`}
            onOpen={() => {
              if (announcement.read) return;
              void markRead({
                variables: { input: { announcementIds: [announcement.id], read: true } },
              });
            }}
          />
        ))}

        {/* The panel is capped at the most recent few, so this is the way to the archive. */}
        {announcements.length > 0 && (
          <Box sx={{ pb: 1, textAlign: 'center' }}>
            <MuiLink
              href={`/networks/${currentNetwork.id}/announcements`}
              target="_blank"
              rel="noopener"
              sx={{ fontSize: '0.875rem' }}
            >
              {t('components.announcements.view_all')}
            </MuiLink>
          </Box>
        )}
      </Box>
    </LoadingPane>
  );
};
